// Scope: coordinates fetching, pagination, editing, archiving, and restoring MERN notes.

import React, { useState, useEffect, useCallback } from "react";
import Note from "./Note";
import CreateArea from "./CreateArea";
import noteStore from "../../noteStore";
import ShowNote from "./showNote";
import {
    getNotes,
    createNewNote,
    archiveNote,
    restoreNote,
    updateNote,
} from "../../Services/server-api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Dashboard from "./dashboard";
import NoteSkeleton from "./Helper/NoteSkeleton";

function NotesList({ selectedOption, socket, activeUsers }) {
    const { notePage, setNotePage, setNotes_all } = noteStore();
    const notesPerPage = selectedOption;
    const [displayedNote, setDisplayedNote] = useState(null);
    const [view, setView] = useState("active");

    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const suppressUntilRef = React.useRef(0);

    const fetchNotes = useCallback(async (requestedView = view) => {
        try {
            const cacheKey = `notes_cache_${requestedView}`;
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    setNotes(parsed);
                    setNotes_all(parsed);
                    setIsLoading(false);
                } catch (e) {
                    // ignore parse errors
                }
            }

            const fetchedNotes = await getNotes(requestedView);
            setNotes(fetchedNotes);
            setNotes_all(fetchedNotes);
            try { localStorage.setItem(cacheKey, JSON.stringify(fetchedNotes)); } catch (e) {}
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setIsLoading(false);
            toast.error("Notes could not be loaded.");
        }
    }, [setNotes_all, view]);

    useEffect(() => {
        // Set up socket event listeners
        const handler = (message) => {
            // If we recently performed a local update, suppress the toast to avoid duplicates
            const now = Date.now();
            if (suppressUntilRef.current && now < suppressUntilRef.current) {
                // still refresh notes silently
                fetchNotes();
                return;
            }
            fetchNotes();
            toast.warning(message);
        };

        socket && socket.on("updatePage", handler);

        return () => {
            socket && socket.off("updatePage", handler);
        };
    }, [socket, fetchNotes]);

    useEffect(() => {
        setIsLoading(true);
        setDisplayedNote(null);
        setNotePage(1);
        fetchNotes(view);
    }, [view, fetchNotes, setNotePage]);

    async function addNote(newNote) {
        try {
            // Create the new note and wait for the response
            const createNoteResponse = await createNewNote(newNote);

            // Check if the note creation was successful
            if (createNoteResponse.success) {
                // Note was created successfully, now fetch the updated notes
                await fetchNotes();
                toast.success("Note created successfully!");
                // suppress incoming "updatePage" toast for 1s since we just updated
                suppressUntilRef.current = Date.now() + 1000;
                socket && socket.emit("newNote", "Refresh Required!");
            } else {
                // Note creation failed, handle the error (optional)
                toast.error("Failed to create the note.");
            }
        } catch (error) {
            // Handle any unexpected errors
            console.error("Error creating the note:", error);
            toast.error("An error occurred while creating the note.");
        }
    }

    const handleArchiveNote = async (noteId) => {
        try {
            const response = await archiveNote(noteId);

            if (response.status === 200) {
                toast.info("Note archived. You can restore it from Archived notes.");

                await fetchNotes();
                socket && socket.emit("archiveNote");
            } else {
                toast.error("Note not found");
            }
        } catch (error) {
            toast.error("An error occurred while archiving the note.");
        }
    };

    const handleRestoreNote = async (noteId) => {
        try {
            const response = await restoreNote(noteId);
            if (response.status === 200) {
                toast.success("Note restored to Active notes.");
                await fetchNotes();
                socket && socket.emit("updateNote", "A note has been restored.");
            }
        } catch (error) {
            toast.error("An error occurred while restoring the note.");
        }
    };

    // Function to handle the "Save" button click in the ShowNote component
    // Function to update a note
    const handleSaveNote = async (noteId, updatedFields) => {
        try {
            // Update the note and wait for the response
            const response = await updateNote(noteId, updatedFields);

            // Check the status code of the response
            if (response.status === 200) {
                toast.info(response.data.message);

                // Fetch the updated notes after successful update
                await fetchNotes();
                // suppress incoming "updatePage" toast for 1s since we just updated
                suppressUntilRef.current = Date.now() + 1000;
                socket && socket.emit("updateNote", "Refresh Required!");
                return true;
            } else {
                toast.error(response.message);
                return false;
            }
        } catch (error) {
            // Handle any unexpected errors
            console.error("Error updating the note:", error);
            toast.error("An error occurred while updating the note.");
            return false;
        }
    };

    // Calculate the index range for the current page
    const indexOfLastNote = notePage * notesPerPage;
    const indexOfFirstNote = indexOfLastNote - notesPerPage;
    const currentNotes = notes.slice(indexOfFirstNote, indexOfLastNote);

    // Calculate the total number of pages
    const totalPages = Math.ceil(notes.length / notesPerPage);

    // Function to handle page change
    const handlePageChange = (pageNumber) => {
        setNotePage(pageNumber);
    };

    // Function to display a single note in a popup
    const displayNote = (noteId) => {
        // Get the note data from the currentNotes array using the index
        const noteToDisplay = notes.find((note) => note._id === noteId);
        // Set the note data in the state to display it in the popup
        setDisplayedNote(noteToDisplay);
        console.log(displayedNote);
    };

    // Function to close the popup and clear the displayedNote state
    const closePopup = () => {
        setDisplayedNote(null);
    };

    return (
        <div>
            <Dashboard notes={notes} activeUsers={activeUsers} view={view} />
            <div className="notes-view-toggle" role="group" aria-label="Note status">
                <button type="button" className={view === "active" ? "active" : ""} onClick={() => setView("active")}>
                    Active notes
                </button>
                <button type="button" className={view === "deleted" ? "active" : ""} onClick={() => setView("deleted")}>
                    Archived notes
                </button>
            </div>
            {view === "active" && <CreateArea addNote={addNote} />}
            {/* Notes display Section */}
            {/* <div className="notes-display" >
 
        {currentNotes.map((data, index) => (
          <Note
              key={data._id}
              title={data.title}
              content={data.content}
              status={data.status}
              timeCreated={data.timeCreated}
              removeNote={() => handleDeleteNote(data._id)}
              displayNote={() => displayNote(data._id)}/>
        ))}

      </div> */}
            <div className="notes-display skeleton">
                {isLoading // Conditional rendering of Skeleton while loading
                    ? Array.from({ length: notesPerPage }, (_, index) => (
                          <NoteSkeleton key={index} />
                      ))
                    : currentNotes.length === 0
                    ? <div className="notes-empty-state">
                          <h2>{view === "deleted" ? "No archived notes" : "No active notes yet"}</h2>
                          <p>{view === "deleted" ? "Archived notes will stay recoverable here." : "Create a note to get started."}</p>
                      </div>
                    : currentNotes.map((data) => (
                          <Note
                              key={data._id}
                              title={data.title}
                              content={data.content}
                              status={data.status}
                              timeCreated={data.timeCreated}
                              archiveNote={() => handleArchiveNote(data._id)}
                              restoreNote={() => handleRestoreNote(data._id)}
                              displayNote={() => displayNote(data._id)}
                          />
                      ))}
            </div>
            {displayedNote && (
                <ShowNote
                    _id={displayedNote._id}
                    title={displayedNote.title}
                    content={displayedNote.content}
                    timeCreated={displayedNote.timeCreated}
                    closeNote={closePopup}
                    handleSave={handleSaveNote}
                />
            )}
            ;{/* Pagination */}
            <div className="pagination">
                {Array.from({ length: totalPages }, (_, index) => (
                    <button
                        key={index}
                        onClick={() => handlePageChange(index + 1)}
                        className={notePage === index + 1 ? "active" : ""}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default NotesList;
